import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, Suspense, useEffect, useState } from 'react';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import PageTitle from '@helpers/PageTitle';
import { useUpdateUserState, useUserState } from '@context/UserContext';
import { useLocalAppContext } from '@context/LocalAppContext';
import BrandLandingPage from '@domain/BrandLandingPage/BrandLandingPage';
import Header from '@domain/Header/Header';
import AccountScreen from '@domain/Account/AccountScreen';
import PrivateRoute from '@components/Route/PrivateRoute';
import useTrackNavigation from '@hooks/useTrackNavigation';
import EmailConfirmationAuthentication from '@components/Modals/AuthenticationModal/Components/Register/EmailConfirmationAuthentication';
import ErrorPage from '@domain/Errors/components/ErrorPage';
import PlatformScreen from '@domain/Platform/PlatformScreen';
import CompeteScreen from '@domain/Platform/CompeteScreen';
import { useFeature } from 'flagged';
import { styled } from '@mui/material/styles';
import PlatformGamesMenu from '@domain/Header/components/Menus/PlatformGamesMenu';
import DisabledMarketplaceScreen from '@domain/Marketplace/components/MarketplaceHub/components/DisabledMarketplaceScreen';
import useTrackPageView from '@hooks/useTrackPageView';
import useRootRedirectUrl from '@hooks/useRootRedirectUrl';
import LandingPageScreen from '@domain/LandingPage/LandingPageScreen';
import getAddGameCallbackParams from '@helpers/getAddGameCallbackParams';
import getMobileAddGameWebViewConfig from '@helpers/getMobileAddGameWebViewConfig';
import { markMobileAppWebView } from '@hooks/useAddGameWebView';
import CompleteEmailVerificationPage from '@domain/Account/Registration/CompleteEmailVerificationPage';
import useLocalStorage from '@components/Hooks/useLocalStorage';
import ChallengeLandingPage from '@domain/Challenge/ChallengeLandingPage';
const Error404 = lazy(() => import('./domain/Errors/components/Error404'));
const UserScreen = lazy(() => import('./domain/User/UserScreen'));
const PlaystationScreen = lazy(() => import('./domain/Platform/PlaystationScreen'));
const RewardsPage = lazy(() => import('./domain/Rewards/RewardsPage'));
const MarketplaceScreen = lazy(() => import('@domain/Marketplace/MarketplaceScreen'));
const InfluencerPageRouter = lazy(() => import('./domain/InfluencerPage/InfluencerPageRouter'));
const TournamentHomepage = lazy(() => import('@domain/Tournament/TournamentHomepage/TournamentHomepage'));
const GameScreen = lazy(() => import('@domain/Tournament/GameScreen'));
const ChangeEmailConfirmationScreen = lazy(() => import('@domain/Account/ChangeEmailConfirmation'));
const RegistrationConfirmation = lazy(() => import('@components/Modals/AuthenticationModal/Components/Register/RegistrationConfirmation'));
const AboutPage = lazy(() => import('@domain/About/AboutPage'));
const CompleteRegistrationPage = lazy(() => import('@domain/Account/Registration/CompleteRegistrationPage'));
const TipaltiWithdrawalPage = lazy(() => import('@domain/Account/Withdrawal/TipaltiWithdrawalPage'));
const Container = styled('div') `
  font-family: ${({ theme }) => theme.typography.fontFamily};
`;
const MainContent = styled('main') `
  display: flex;

  & > div:last-of-type {
    flex-grow: 1;
    min-width: 0px;
  }
`;
/**
 * WARNING: All pages need to have a single parent component (ie, a wrapping div) so that the 'MainContent' flex
 * renders the content next to the platform first games menu when displayed
 */
const RepeatAppScreen = () => {
    var _a, _b;
    const { registrationNotComplete, userProfile, userDetails, authenticated } = useUserState();
    const { setUserDetailsCache } = useUpdateUserState();
    const isPlayStationPlatform = navigator.userAgent.includes('PlayStation');
    useTrackNavigation();
    const { trackPageView } = useTrackPageView();
    const isMarketplaceDisabled = !!useFeature('disable_marketplace');
    const isPlatformFirstEnabled = !!useFeature('enable_platform_first');
    const isCoinBonusesDisabled = !!useFeature('disable_coin_bonuses');
    const location = useLocation();
    const addGameStatus = getAddGameCallbackParams(location.search);
    const { openModal } = useLocalAppContext();
    const { pathname } = useLocation();
    const [, setCompleteRegistrationRedirectUrl] = useLocalStorage('complete-registration-initial-route', null);
    const history = useHistory();
    const [hasOpenedMobileAddGameModal, setHasOpenedMobileAddGameModal] = useState(false);
    const mobileAddGameWebViewConfig = getMobileAddGameWebViewConfig(pathname, location.search, !!window.ReactNativeWebView);
    useEffect(() => {
        if (addGameStatus === null || addGameStatus === void 0 ? void 0 : addGameStatus.gameKey) {
            openModal({
                type: 'add-game',
                gameKey: addGameStatus.gameKey,
                onClose: () => { },
                successButtonLabel: addGameStatus.successButtonLabel,
            });
        }
    }, [addGameStatus === null || addGameStatus === void 0 ? void 0 : addGameStatus.gameKey]);
    useEffect(() => {
        if (!mobileAddGameWebViewConfig) {
            return;
        }
        markMobileAppWebView();
        if (!hasOpenedMobileAddGameModal && !(addGameStatus === null || addGameStatus === void 0 ? void 0 : addGameStatus.gameKey)) {
            openModal({
                type: 'add-game',
                gameKey: mobileAddGameWebViewConfig.gameKey,
                filterByPlatform: mobileAddGameWebViewConfig.filterByPlatform,
                onClose: () => { },
            });
            setHasOpenedMobileAddGameModal(true);
        }
    }, [mobileAddGameWebViewConfig, hasOpenedMobileAddGameModal, openModal, addGameStatus === null || addGameStatus === void 0 ? void 0 : addGameStatus.gameKey]);
    useEffect(() => {
        if (!mobileAddGameWebViewConfig && hasOpenedMobileAddGameModal) {
            setHasOpenedMobileAddGameModal(false);
        }
    }, [mobileAddGameWebViewConfig, hasOpenedMobileAddGameModal]);
    const rootRedirectUrl = useRootRedirectUrl();
    useEffect(() => {
        var _a;
        if (!pathname.includes('/register/complete-profile') && registrationNotComplete) {
            history.push('/register/complete-profile');
            if (!pathname.includes('/register')) {
                setCompleteRegistrationRedirectUrl(pathname);
            }
        }
        else if (!pathname.includes('/register/confirm') &&
            !pathname.includes('/register/verify') &&
            authenticated &&
            userDetails &&
            userProfile &&
            ((_a = userDetails.verification) === null || _a === void 0 ? void 0 : _a.hasVerifiedEmail) === false) {
            if (!pathname.includes('/register')) {
                setCompleteRegistrationRedirectUrl(pathname);
            }
            history.push('/register/verify');
        }
        else if (authenticated && pathname === '/') {
            history.push(rootRedirectUrl);
        }
        else {
            trackPageView();
        }
    }, [trackPageView, pathname, authenticated, registrationNotComplete, (_a = userDetails === null || userDetails === void 0 ? void 0 : userDetails.verification) === null || _a === void 0 ? void 0 : _a.hasVerifiedEmail]);
    const unauthenticatedAndRegistrationNotComplete = !authenticated && !registrationNotComplete;
    const authenticatedAndEmailNotVerified = authenticated && ((_b = userDetails === null || userDetails === void 0 ? void 0 : userDetails.verification) === null || _b === void 0 ? void 0 : _b.hasVerifiedEmail) === true;
    const hideMainHeader = unauthenticatedAndRegistrationNotComplete || authenticatedAndEmailNotVerified;
    if (mobileAddGameWebViewConfig) {
        return _jsx(Container, {});
    }
    return (_jsxs(Container, { children: [hideMainHeader && _jsx(Header, {}), _jsxs(MainContent, { children: [hideMainHeader && _jsx(PlatformGamesMenu, {}), _jsx(Sentry.ErrorBoundary, { fallback: (errorData) => _jsx(ErrorPage, { error: errorData.error }), children: _jsx(Suspense, { fallback: _jsx("div", {}), children: _jsxs(Switch, { children: [_jsx(Route, { exact: true, path: ['', '/'], children: _jsx(LandingPageScreen, {}) }), _jsx(Route, { path: "/user", children: _jsxs(_Fragment, { children: [_jsx(PageTitle, {}), _jsx(UserScreen, {})] }) }), _jsx(Route, { exact: true, path: "/brands", children: _jsx(BrandLandingPage, {}) }), _jsx(Route, { path: "/challenge/lobby/:lobbyId?", children: _jsx(ChallengeLandingPage, {}) }), !isCoinBonusesDisabled && (_jsx(PrivateRoute, { exact: true, path: "/coins", children: _jsx(RewardsPage, {}) })), _jsx(Route, { path: "/marketplace", children: isMarketplaceDisabled ? _jsx(DisabledMarketplaceScreen, {}) : _jsx(MarketplaceScreen, {}) }), _jsx(PrivateRoute, { exact: true, path: "/account/transactions/withdrawal", children: _jsx(TipaltiWithdrawalPage, {}) }), _jsx(PrivateRoute, { path: "/account", children: _jsx(AccountScreen, {}) }), _jsx(Route, { path: ['/influencer-page/:slug', '/ip/:slug'], children: _jsx(InfluencerPageRouter, {}) }), _jsx(PrivateRoute, { path: "/register/verify", children: _jsx(CompleteEmailVerificationPage, { updateUser: setUserDetailsCache, email: userDetails === null || userDetails === void 0 ? void 0 : userDetails.email, handle: userProfile === null || userProfile === void 0 ? void 0 : userProfile.handle }) }), _jsx(PrivateRoute, { path: "/register/complete-profile", children: _jsx(CompleteRegistrationPage, { partialUserDetails: userDetails !== null && userDetails !== void 0 ? userDetails : {} }) }), _jsx(PrivateRoute, { path: "/change-email/confirm/:token", children: _jsx(ChangeEmailConfirmationScreen, {}) }), _jsx(Route, { exact: true, path: "/register/confirm/:token", children: _jsx(EmailConfirmationAuthentication, {}) }), _jsx(PrivateRoute, { path: "/register/confirmed", children: _jsx(RegistrationConfirmation, {}) }), _jsx(Route, { path: "/about", children: _jsx(AboutPage, {}) }), isPlatformFirstEnabled && (_jsx(Route, { path: "/platform", children: _jsx(PlatformScreen, {}) })), isPlatformFirstEnabled && (_jsx(Route, { exact: true, path: "/compete", children: _jsx(CompeteScreen, {}) })), isPlatformFirstEnabled && (_jsx(Route, { exact: true, path: "/:platformSlug/compete", children: _jsx(CompeteScreen, {}) })), isPlatformFirstEnabled && isPlayStationPlatform && (_jsx(Route, { exact: true, path: "/playstation", children: _jsx(PlaystationScreen, {}) })), _jsxs(Switch, { children: [_jsx(Route, { exact: true, path: "/tournaments", children: _jsx(TournamentHomepage, {}) }), _jsx(Route, { path: "/:slug", children: _jsx(GameScreen, {}) })] }), _jsx(Route, { path: "/", children: _jsx(Error404, {}) })] }) }) })] })] }));
};
export default RepeatAppScreen;
